// dependencies
import { format, isValid, parseISO } from "date-fns";
import React from "react";

import { faCircleXmark } from "@fortawesome/pro-solid-svg-icons";

// components
import Container from "../../../../../components/Container";
import Icon from "../../../../../components/Icon";
import Person from "../../../../../components/Person";
import Spacer from "../../../../../components/Spacer";
import Text from "../../../../../components/Text";

// utils
import { getViewableField } from "../../../utils";

// styles
import css from "../../ViewFieldHistory.module.scss";

interface BaseProps {
	historyItem: any;
	fieldType:
		| "phone"
		| "person"
		| "monetary"
		| "numeric"
		| "email"
		| "single-select"
		| "textbox"
		| "text"
		| "multi-select"
		| "people"
		| "date"
		| "department"
		| "unknown";
	isEditable: boolean;
	onRemoveHistoryItem: (key: string) => void;
}

const getFormattedDate = (date: string | undefined) => {
	if (!date) return "";

	const parsedDate = parseISO(date);
	if (!isValid(parsedDate)) {
		return "Invalid date";
	}

	return format(parsedDate, "MMMM d, yyyy • h:mmaaa");
};

const FieldHistoryItem = (props: BaseProps) => {
	const { historyItem, fieldType, onRemoveHistoryItem, isEditable } = props;
	const { _id, asOf, updatedOn, updater, value, comment } = historyItem;

	const getTestId = () => {
		const valueStr = String(historyItem.value || "blank-value")
			.toLowerCase()
			.replace(/\s/g, "-");
		const parsedAsOf = parseISO(asOf);
		const dateStr = isValid(parsedAsOf)
			? parsedAsOf.toLocaleDateString("en-US").replace(/\//g, "-")
			: "invalid-date";
		return `${valueStr}-history-item-${dateStr}`;
	};

	return (
		<Container column className={css.FieldHistoryItem} testId={getTestId()}>
			<Container horizontalAlignment="space-between">
				<Container gap="16px">
					<Container gap="4px">
						<Text size="paragraph-3" weight="semi-bold">
							Effective Date:
						</Text>
						<Text size="paragraph-3" weight="regular">
							{getFormattedDate(asOf)}
						</Text>
					</Container>
					<Container gap="4px">
						<Text size="paragraph-3" weight="semi-bold">
							Actual Date:
						</Text>
						<Text size="paragraph-3" weight="regular">
							{getFormattedDate(updatedOn)}
						</Text>
					</Container>
				</Container>

				{isEditable ? (
					<div onClick={() => onRemoveHistoryItem(_id)}>
						<Icon className={css.iconButton} icon={faCircleXmark} size="small" />
					</div>
				) : null}
			</Container>
			<Spacer height={2} />
			<Container verticalAlignment="center" gap="4px" wrap>
				<Text size="paragraph-3" weight="regular">
					Action:
				</Text>
				{updater && updater.name ? (
					<Person
						id={updater._id}
						className={css.updaterAvatar}
						avatarSize={20}
						name={updater.name}
						fontSize={14}
					/>
				) : (
					<Text size="paragraph-3" weight="regular">
						Someone
					</Text>
				)}
				<Text size="paragraph-3" weight="regular">
					updated the value to be
				</Text>
				<Text size="paragraph-3" weight="semi-bold">
					{getViewableField(fieldType, value)}
				</Text>
			</Container>
			{comment !== undefined && comment !== "" ? (
				<>
					<Spacer height={2} />
					<Container column gap="8px" className={css.commentBox}>
						<Text size="caption" weight="semi-bold">
							Comment:
						</Text>
						<Text size="paragraph-3" weight="regular">
							{comment}
						</Text>
					</Container>
				</>
			) : null}
			<Spacer height={3} />
		</Container>
	);
};

export default FieldHistoryItem;
